import Phaser from 'phaser';
import { TILE_SIZE } from '@/config/constants';
import { dotRadiusFor } from '@/services/dotLayoutGenerator';
import type { TileSpec } from '@/types/models';

const TILE_DEPTH = 100;

export class TileSprite extends Phaser.GameObjects.Container {
  public readonly spec: TileSpec;
  public readonly halfSize = TILE_SIZE / 2;
  private bg: Phaser.GameObjects.Graphics;
  private highlight: Phaser.GameObjects.Graphics;
  private trail: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  public vx = 0;
  public vy = 0;
  public gravity = 0;
  /** Gravedad aplicada solo al ir bajando (vy>=0). null = usa `gravity`. */
  public gravityDown: number | null = null;
  /** Velocidad máxima de caída (px/s). Infinity = sin tope. */
  public terminalVy = Infinity;
  public spawnedAt: number;
  public consumed = false;

  constructor(scene: Phaser.Scene, spec: TileSpec, x: number, y: number) {
    super(scene, x, y);
    this.spec = spec;
    this.spawnedAt = scene.time.now;

    const size = TILE_SIZE;

    this.bg = scene.add.graphics();
    this.bg.fillStyle(spec.color, 1);
    this.bg.fillRoundedRect(-size / 2, -size / 2, size, size, 28);
    this.bg.lineStyle(4, 0xffffff, 0.18);
    this.bg.strokeRoundedRect(-size / 2, -size / 2, size, size, 28);

    this.highlight = scene.add.graphics();
    this.highlight.fillStyle(0xffffff, 0.12);
    this.highlight.fillRoundedRect(-size / 2 + 6, -size / 2 + 6, size - 12, (size - 12) / 2, 22);

    const dotsLayer = scene.add.graphics();
    const r = dotRadiusFor(spec.dotCount);
    dotsLayer.fillStyle(0xffffff, 0.95);
    for (const p of spec.dotPositions) {
      const x = p.x - size / 2;
      const y = p.y - size / 2;
      dotsLayer.fillCircle(x, y, r);
    }
    dotsLayer.lineStyle(2, 0x000000, 0.18);
    for (const p of spec.dotPositions) {
      const x = p.x - size / 2;
      const y = p.y - size / 2;
      dotsLayer.strokeCircle(x, y, r);
    }

    this.add([this.bg, this.highlight, dotsLayer]);
    this.setSize(size, size);
    this.setDepth(TILE_DEPTH);

    if (scene.textures.exists('particle')) {
      this.trail = scene.add.particles(x, y, 'particle', {
        follow: this,
        scale: { start: 0.7, end: 0 },
        alpha: { start: 0.55, end: 0 },
        speed: { min: 0, max: 30 },
        lifespan: 380,
        frequency: 22,
        tint: spec.color,
        blendMode: Phaser.BlendModes.ADD,
        quantity: 1
      });
      this.trail.setDepth(TILE_DEPTH - 1);
    }

    scene.add.existing(this);
  }

  applyMotion(dt: number): void {
    const dts = dt / 1000;
    const g = this.vy >= 0 && this.gravityDown !== null ? this.gravityDown : this.gravity;
    this.vy += g * dts;
    if (this.vy > this.terminalVy) this.vy = this.terminalVy;
    this.x += this.vx * dts;
    this.y += this.vy * dts;
    this.rotation += this.vx * 0.0004 * dts;
  }

  /**
   * Pixel-perfect circle vs rotated-square collision.
   * Returns true if a circle of radius `r` centered at world (cx, cy)
   * overlaps the visible tile bounds (TILE_SIZE x TILE_SIZE) accounting
   * for the tile's current rotation.
   */
  intersectsCircle(cx: number, cy: number, r: number): boolean {
    const dx = cx - this.x;
    const dy = cy - this.y;
    const cos = Math.cos(-this.rotation);
    const sin = Math.sin(-this.rotation);
    // Transform circle center into tile local frame
    const lx = dx * cos - dy * sin;
    const ly = dx * sin + dy * cos;
    const half = this.halfSize;
    const closestX = Math.max(-half, Math.min(half, lx));
    const closestY = Math.max(-half, Math.min(half, ly));
    const ddx = lx - closestX;
    const ddy = ly - closestY;
    return ddx * ddx + ddy * ddy <= r * r;
  }

  flashCorrect(): void {
    this.consumed = true;
    this.stopTrail(120);
    this.emitExplosion(40, 0xffffff);
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1, to: 1.3 },
      alpha: { from: 1, to: 0 },
      duration: 220,
      ease: 'Quad.easeOut',
      onComplete: () => this.destroy()
    });
  }

  flashWrong(): void {
    this.consumed = true;
    this.stopTrail(60);
    this.emitExplosion(20, 0xf87171);
    this.scene.tweens.add({
      targets: this,
      angle: { from: -10, to: 10 },
      duration: 50,
      yoyo: true,
      repeat: 2,
      onComplete: () => {
        this.scene.tweens.add({
          targets: this,
          alpha: 0,
          scale: 0.6,
          duration: 180,
          onComplete: () => this.destroy()
        });
      }
    });
  }

  private emitExplosion(quantity: number, accentTint: number): void {
    if (!this.scene.textures.exists('particle')) return;
    const burst = this.scene.add.particles(this.x, this.y, 'particle', {
      scale: { start: 1.2, end: 0 },
      alpha: { start: 1, end: 0 },
      speed: { min: 250, max: 700 },
      angle: { min: 0, max: 360 },
      lifespan: { min: 400, max: 700 },
      tint: [this.spec.color, accentTint, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false
    });
    burst.setDepth(TILE_DEPTH + 1);
    burst.explode(quantity);
    this.scene.time.delayedCall(800, () => burst.destroy());
  }

  destroy(fromScene?: boolean): void {
    this.stopTrail(0);
    super.destroy(fromScene);
  }

  private stopTrail(extraDelayMs: number): void {
    if (!this.trail) return;
    const emitter = this.trail;
    this.trail = null;
    emitter.stop();
    this.scene.time.delayedCall(450 + extraDelayMs, () => {
      emitter.destroy();
    });
  }
}
